import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { JournalEntry, User } from "../types";

function date(value: string | null): string {
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric" })
    .format(new Date(value ?? Date.now()));
}

export function PublicHome({ user, onNavigate }: { user: User | null; onNavigate: (path: string) => void }) {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState("");

  const publicTags = useMemo(() => {
    const tags = new Map<string, string>();
    for (const entry of entries) {
      for (const tag of entry.tags) tags.set(tag.toLocaleLowerCase("ru-RU"), tag);
    }
    return [...tags.values()].sort((left, right) => left.localeCompare(right, "ru"));
  }, [entries]);
  const filteredEntries = useMemo(() => selectedTag
    ? entries.filter((entry) => entry.tags.some((tag) => tag.toLocaleLowerCase("ru-RU") === selectedTag.toLocaleLowerCase("ru-RU")))
    : entries, [entries, selectedTag]);

  useEffect(() => {
    if (selectedTag && !publicTags.some((tag) => tag.toLocaleLowerCase("ru-RU") === selectedTag.toLocaleLowerCase("ru-RU"))) {
      setSelectedTag("");
    }
  }, [publicTags, selectedTag]);

  useEffect(() => {
    let active = true;
    async function refreshEntries() {
      try {
        const result = await api.publicJournal();
        if (active) setEntries(result.entries);
      } catch {
        if (active) setEntries([]);
      } finally {
        if (active) setLoading(false);
      }
    }
    function refreshWhenVisible() {
      if (document.visibilityState === "visible") void refreshEntries();
    }
    void refreshEntries();
    window.addEventListener("focus", refreshEntries);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.removeEventListener("focus", refreshEntries);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  return (
    <main className="public-site">
      <header className="public-header">
        <button className="wordmark" onClick={() => onNavigate("/")}>petr osetr</button>
        <nav aria-label="Основная навигация">
          <a href="#logbook">Log book</a>
          <button className="login-link" onClick={() => onNavigate(user ? "/cabinet" : "/login")}>{user ? "Кабинет" : "Войти"} <span>↗</span></button>
        </nav>
      </header>

      <section className="public-hero">
        <p className="site-kicker">ЛИЧНЫЙ САЙТ · LOG BOOK</p>
        <h1>Личный сайт<br />петра осетра.</h1>
        <div className="hero-bottom">
          <p>Избранные записи из дневника и наблюдения, к которым хочется возвращаться.</p>
          <a href="#logbook" className="round-arrow" aria-label="Перейти к записям">↓</a>
        </div>
      </section>

      <section className="logbook-section" id="logbook">
        <div className="public-entries">
          {loading && <p className="empty-note">Загружаю записи…</p>}
          {!loading && entries.length === 0 && <p className="empty-note">Первая открытая запись скоро появится.</p>}
          {!loading && publicTags.length > 0 && <div className="public-tag-filter" aria-label="Поиск записей по тегам">
            <button type="button" className={!selectedTag ? "active" : ""} onClick={() => setSelectedTag("")}>Все записи</button>
            {publicTags.map((tag) => <button type="button" className={selectedTag.toLocaleLowerCase("ru-RU") === tag.toLocaleLowerCase("ru-RU") ? "active" : ""} key={tag} onClick={() => setSelectedTag(tag)}>#{tag}</button>)}
          </div>}
          {!loading && entries.length > 0 && filteredEntries.length === 0 && <p className="empty-note">По этому тегу записей нет.</p>}
          {filteredEntries.map((entry, index) => (
            <article className="public-entry" key={entry.id}>
              <div className="entry-meta"><span>{String(index + 1).padStart(2, "0")}</span><time>{date(entry.createdAt)}</time></div>
              <h3>{entry.title}</h3>
              {entry.tags.length > 0 && <div className="public-entry-tags">{entry.tags.map((tag) => <button type="button" key={tag} onClick={() => setSelectedTag(tag)}>#{tag}</button>)}</div>}
              <div className="entry-body">{entry.body.split(/\n{2,}/).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
            </article>
          ))}
        </div>
      </section>

      <footer className="public-footer"><span>© {new Date().getFullYear()} петр осётр</span><span>petrosetr.com</span></footer>
    </main>
  );
}
